/*
================================================================================
File: src/pages/Settings/MyCompany.js
Description: Form for updating the user's company information.
================================================================================
*/
import React, { useState } from "react";
import instance from "../../api/axios";
import PageContent from "../../components/common/PageContent";

// SVG Icons
const BuildingIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.23.613a16.028 16.028 0 006.837 6.837l.612-1.23a1 1 0 011.06-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
  </svg>
);

const MapPinIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
  </svg>
);

const MyCompany = ({ pulldata, user }) => {
  const [displayName, setDisplayName] = useState(user?.co_name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [number, setNumber] = useState(user?.phone || "");
  const [fax, setFax] = useState(user?.co_fax || "");
  const [mailing, setMailing] = useState(user?.company_address || "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData();
    formData.append("owner", user?.app_id);
    formData.append("co_name", displayName);
    formData.append("comp_fax", fax);
    formData.append("email", email);
    formData.append("number", number);
    formData.append("comp_address", mailing);
    formData.append("type", "company");
    formData.append("bio", "");

    try {
      await instance.post("/updateProfile.php", formData);
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error updating company info: " + (e.response?.data?.message || e.message));
        console.error("Error updating company info: ", e.response?.data?.message || e.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setIsLoading(false);
            await pulldata(user?.app_id);
    }
  };

  return (
    <PageContent
      title="Profile Settings"
      subtitle="Company Profile"
      error={error}
    >
      <div className="p-8 space-y-6">
        {/* Hero Banner */}
        <div className="bg-blue-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-4 mb-2">
            <div className="p-3 bg-white/20 rounded-lg">
              <BuildingIcon />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{user?.co_name || "Your Company"}</h2>
              <p className="text-blue-100">Keep your company contact info up to date.</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Section A: Contact Information */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                <PhoneIcon />
              </div>
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Contact Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
            <div>
              <label
                htmlFor="displayName"
                className="block text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                Company Name:
              </label>
              <input
                type="text"
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
            </div>
            <div>
              <label
                htmlFor="emailAddress"
                className="block text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                Company E-Mail Address:
              </label>
              <input
                type="email"
                id="emailAddress"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
            </div>
            <div>
              <label
                htmlFor="number"
                className="block text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                Company Number:
              </label>
              <input
                type="tel"
                id="number"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="1234567890"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
            </div>
            <div>
              <label
                htmlFor="fax"
                className="block text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                Company Fax:
              </label>
              <input
                type="tel"
                id="fax"
                value={fax}
                onChange={(e) => setFax(e.target.value)}
                placeholder="1234567890"
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
              />
            </div>
            </div>
            </div>

          {/* Section B: Mailing Address */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                <MapPinIcon />
              </div>
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Mailing Address</h3>
            </div>
            <div>
              <label
                htmlFor="mailing"
                className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2"
              >
                Company Mailing Address
              </label>
              <textarea
                id="mailing"
                rows="4"
                value={mailing}
                onChange={(e) => setMailing(e.target.value)}
                className="block p-3 w-full text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                placeholder="123 Main St, Anytown, USA 12345"
              />
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="px-5 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </PageContent>
  );
};

export default MyCompany;
